import BaseApiService from './BaseApiService.js';

class StudentService extends BaseApiService {

  // ✅ Get all students
  async getStudents(orgId) {
    const url = orgId ? `/api/student/all?orgIdParam=${encodeURIComponent(orgId)}` : '/api/student/all';
    return this.makeRequest(url, { method: 'GET' });
  }

  // ✅ Create/Register a new student
  async createStudent(studentData) {
    console.log('🚀 StudentService.createStudent() - Sending to API:', studentData);
    console.log('🚀 OrgId value:', studentData.orgId, 'Type:', typeof studentData.orgId);
    return this.makeRequest('/api/student/register', {
      method: 'POST',
      body: JSON.stringify(studentData),
    });
  }

  // ✅ Update student information
  async updateStudent(id, studentData) {
    return this.makeRequest(`/api/student/update/${id}`, {
      method: 'PUT',
      body: JSON.stringify(studentData),
    });
  }

  // ✅ Delete a student
  async deleteStudent(id) {
    return this.makeRequest(`/api/student/delete/${id}`, { method: 'DELETE' });
  }


  // ✅ Get student by ID
  async getStudentById(studentId) {
    return this.makeRequest(`/api/student/${studentId}`, { method: 'GET' });
  }

  // ✅ Bulk reset student and parent passwords
  async bulkResetPasswords(orgId, password) {
    return this.makeRequest('/api/student/bulk-reset-passwords', {
      method: 'POST',
      body: JSON.stringify({ orgId, password }),
    });
  }

  // ✅ Admin reset single student password
  async adminResetStudentPassword(studentId, password) {
    return this.makeRequest(`/api/student/${studentId}/admin-reset-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  // ✅ Search students by name or other criteria
  async searchStudents(searchTerm) {
    return this.makeRequest(`/api/student/search?q=${encodeURIComponent(searchTerm)}`, { 
      method: 'GET' 
    });
  }

  // ✅ Get students by course
  async getStudentsByCourse(courseId) {
    return this.makeRequest(`/api/student/course/${courseId}`, { method: 'GET' });
  }

  // ✅ Get students by semester
  async getStudentsBySemester(semester) {
    return this.makeRequest(`/api/student/semester/${semester}`, { method: 'GET' });
  }

  // ✅ Get students by department
  async getStudentsByDepartment(departmentId) {
    return this.makeRequest(`/api/student/department/${departmentId}`, { method: 'GET' });
  }

  // QR-based student pre-registration (public/student side)
  async preRegisterStudentByQr(orgId, studentData) {
    return this.makeRequest(`/api/student/pre-register/${orgId}`, {
      method: 'POST',
      body: JSON.stringify(studentData),
    });
  }

  // Get organization details (public)
  async getOrganizationDetails(orgId) {
    return this.makeRequest(`/api/student/organization/${orgId}`, { method: 'GET' });
  }

  // Admin: get pending registrations
  async getPendingRegistrations() {
    return this.makeRequest('/api/student/pending', { method: 'GET' });
  }

  // Admin: approve/reject student registration
  async verifyRegistration(studentId, approved, remarks = '') {
    return this.makeRequest(`/api/student/verify/${studentId}`, {
      method: 'PUT',
      body: JSON.stringify({ approved, remarks }),
    });
  }

  // Get promotion settings
  async getPromotionSettings() {
    return this.makeRequest('/api/promotion/settings', { method: 'GET' });
  }

  // Update promotion settings
  async updatePromotionSettings(settings) {
    return this.makeRequest('/api/promotion/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // Get single student promotion eligibility
  async getPromotionEligibility(studentId) {
    return this.makeRequest(`/api/promotion/${studentId}/eligibility`, { method: 'GET' });
  }

  // Promote single student
  async promoteStudent(studentId, remarks = '') {
    return this.makeRequest(`/api/promotion/${studentId}/promote`, {
      method: 'POST',
      body: JSON.stringify({ remarks }),
    });
  }

  // Rollback promotion
  async rollbackPromotion(historyId, rollbackReason = '') {
    return this.makeRequest(`/api/promotion/history/${historyId}/rollback`, {
      method: 'POST',
      body: JSON.stringify({ rollbackReason }),
    });
  }

  // Get student promotion history
  async getPromotionHistory(studentId) {
    return this.makeRequest(`/api/promotion/${studentId}/history`, { method: 'GET' });
  }

  // Get bulk promotion preview
  async getBulkPromotionPreview(currentLevel) {
    return this.makeRequest(`/api/promotion/bulk/preview?currentLevel=${encodeURIComponent(currentLevel)}`, { method: 'GET' });
  }

  // Execute bulk promotion
  async executeBulkPromotion(currentLevel, remarks = '') {
    return this.makeRequest('/api/promotion/bulk/execute', {
      method: 'POST',
      body: JSON.stringify({ currentLevel, remarks }),
    });
  }

  // Get student attendance history (for teacher/admin)
  async getStudentAttendanceHistory(studentId, startDate = '', endDate = '') {
    let url = `/api/student/attendance/history/${studentId}`;
    const params = [];
    if (startDate) params.push(`startDate=${startDate}`);
    if (endDate) params.push(`endDate=${endDate}`);
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    return this.makeRequest(url, { method: 'GET' });
  }
}

// ✅ Export single instance
const studentService = new StudentService();
export default studentService;
